import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { eur, num } from "@/lib/api";
import { openPdfBlob } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileSpreadsheet, RotateCcw, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PAGE = 100;
const fmtDate = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); return s.split("-").reverse().join("/"); };
const eurN = (v) => (v == null ? "—" : eur(v));
const pct = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
const qty = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));

function ProductTable({ title, rows, kind, testid }) {
  if (!rows || rows.length === 0) return null;
  const tot = rows.reduce((a, r) => a + (r.prezzo_totale || 0), 0);
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-primary">{title}</span>
        <Badge variant="secondary" className="text-[10px]">{rows.length}</Badge>
        <span className="ml-auto text-xs font-semibold">{eur(tot)}</span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/60">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-semibold">Codice</th>
              <th className="px-2 py-1.5 font-semibold">Nome</th>
              {kind !== "servizi" && <th className="px-2 py-1.5 font-semibold">Marca</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Q.tà</th>
              {kind === "articoli" && <th className="px-2 py-1.5 font-semibold">UM</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Prezzo unit.</th>
              {kind === "moduli" && <th className="px-2 py-1.5 text-right font-semibold">€/W</th>}
              {kind === "moduli" && <th className="px-2 py-1.5 text-right font-semibold">kWp</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Sconto %</th>
              <th className="px-2 py-1.5 text-right font-semibold">Totale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border" data-testid={`${testid}-row`}>
                <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">{r.codice || "—"}</td>
                <td className="px-2 py-1">{r.nome}</td>
                {kind !== "servizi" && <td className="px-2 py-1 text-muted-foreground">{r.marca || "—"}</td>}
                <td className="px-2 py-1 text-right font-mono">{qty(r.quantita)}</td>
                {kind === "articoli" && <td className="px-2 py-1 text-muted-foreground">{r.um || "—"}</td>}
                <td className="px-2 py-1 text-right font-mono">{eurN(r.prezzo_unitario)}</td>
                {kind === "moduli" && <td className="px-2 py-1 text-right font-mono">{eurN(r.prezzo_watt)}</td>}
                {kind === "moduli" && <td className="px-2 py-1 text-right font-mono">{qty(r.kwp)}</td>}
                <td className="px-2 py-1 text-right font-mono">{r.sconto_pct ? pct(r.sconto_pct) : "—"}</td>
                <td className="px-2 py-1 text-right font-mono font-semibold">{eurN(r.prezzo_totale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderDetail({ orderId, canMargin }) {
  const [det, setDet] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/orders/${orderId}`).then((r) => { if (alive) setDet(r.data); }).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [orderId]);

  const openPdf = async () => {
    try {
      const r = await api.get(`/orders/${orderId}/pdf`, { responseType: "blob" });
      openPdfBlob(r.data, `ordine_${orderId}.pdf`);
    } catch { toast.error("PDF non disponibile per questo ordine"); }
  };

  if (err) return <div className="p-4 text-sm text-muted-foreground">Dettaglio non disponibile.</div>;
  if (!det) return <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico dettaglio…</div>;

  const Info = ({ label, value, testid }) => (
    <div><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div><div className="text-sm font-semibold" data-testid={testid}>{value ?? "—"}</div></div>
  );

  return (
    <div className="space-y-4 p-4" data-testid={`order-detail-${orderId}`}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
        {det.crm_numero && <Info label="Codice CRM" value={det.crm_numero} testid="od-crm-numero" />}
        <Info label="N° Ordine" value={det.numero_ordine} testid="od-numero" />
        <Info label="Preventivo" value={det.order_ref} testid="od-preventivo" />
        <Info label="Stato" value={det.stato} testid="od-stato" />
        <Info label="Modalità pagamento" value={det.modalita_pagamento} testid="od-pagamento" />
        <Info label="Aliquota IVA" value={det.aliquota_iva != null ? `${det.aliquota_iva}%` : "—"} testid="od-iva" />
        <Info label="Importo con IVA" value={det.importo_con_iva_eur != null ? eur(det.importo_con_iva_eur) : "—"} testid="od-imponibile" />
      </div>

      {det.crm_scostamento && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive" data-testid="od-scostamento">
          Attenzione: scostamento tra il preventivo CRM ({det.crm_numero}) e l'ordine Fier (importi o quantità diversi). Fanno fede i dati dell'ordine Fier.
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-primary/30 bg-primary/5 p-3 sm:grid-cols-5" data-testid="od-margine-box">
        {canMargin && <Info label="Margine commessa" value={pct(det.margine_commessa_pct)} testid="od-margine" />}
        {canMargin && <Info label="Margine target" value={pct(det.margine_target_pct)} testid="od-margine-target" />}
        {canMargin && <Info label="Provvigione" value={pct(det.provvigione_pct)} testid="od-provvigione" />}
        {canMargin && <Info label="Sconto cliente" value={pct(det.sconto_cliente_pct)} testid="od-sconto" />}
        <Info label="Indice di contribuzione" value={det.indice_contribuzione ?? "—"} testid="od-indice" />
      </div>

      <ProductTable title="Moduli" kind="moduli" rows={det.moduli} testid="od-tab-moduli" />
      <ProductTable title="Articoli" kind="articoli" rows={det.articoli} testid="od-tab-articoli" />
      <ProductTable title="Servizi e Opzioni" kind="servizi" rows={det.servizi} testid="od-tab-servizi" />

      {det.has_pdf ? (
        <Button size="sm" variant="outline" onClick={openPdf} data-testid={`order-pdf-btn-${orderId}`}>
          <FileDown className="mr-2 h-4 w-4" /> Scarica PDF ordine
        </Button>
      ) : (
        <div className="text-xs text-muted-foreground">Nessun PDF (preventivo firmato) disponibile per questo ordine.</div>
      )}
    </div>
  );
}

export default function Orders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [f, setF] = useState({ agency_id: "all", from_month: "", to_month: "", search: "" });
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const { user } = useAuth();
  const canMargin = ["admin", "back_office", "commercial_manager"].includes(user?.role);

  const seqRef = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const seq = ++seqRef.current;
    const q = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
    if (f.agency_id !== "all") q.append("agency_id", f.agency_id);
    if (f.from_month) q.append("from_month", f.from_month);
    if (f.to_month) q.append("to_month", f.to_month);
    if (f.search.trim()) q.append("search", f.search.trim());
    api.get(`/orders?${q.toString()}`)
      .then((r) => { if (seq === seqRef.current) setData(r.data); })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
  }, [f, page]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {}); }, []);

  const setField = (k) => (v) => { setPage(0); setExpanded(null); setF((s) => ({ ...s, [k]: v })); };
  const reset = () => { setPage(0); setExpanded(null); setF({ agency_id: "all", from_month: "", to_month: "", search: "" }); };

  const exportCsv = async () => {
    const q = new URLSearchParams({ limit: "100000", offset: "0" });
    if (f.agency_id !== "all") q.append("agency_id", f.agency_id);
    if (f.from_month) q.append("from_month", f.from_month);
    if (f.to_month) q.append("to_month", f.to_month);
    if (f.search.trim()) q.append("search", f.search.trim());
    const r = await api.get(`/orders?${q.toString()}`);
    const cols = ["numero_ordine", "date", "cliente", "agency_name", "articolo", "kwp", "importo_pannelli_eur", "importo_accessori_eur", "importo_eur", ...(canMargin ? ["margine_commessa_pct", "margine_eur"] : []), "modalita_pagamento"];
    const head = ["N° Ordine", "Data", "Cliente", "Agenzia", "Articolo", "kWp", "Pannelli €", "Accessori/Servizi €", "Totale €", ...(canMargin ? ["Margine %", "Margine €"] : []), "Pagamento"];
    const rows = (r.data.orders || []).map((o) => cols.map((c) => `"${(o[c] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "archivio_ordini_torri.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.totals;
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const COLS = canMargin ? 10 : 9;

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Archivio Ordini</h1>
          <p className="text-sm text-muted-foreground">Storico ordini Torri · aggiornato automaticamente a ogni import vendite. Clicca una riga per il dettaglio articoli, margine e PDF.</p>
        </div>
        {user?.role !== "agency" && (
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="orders-export-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Esporta CSV
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-2" data-testid="orders-filters">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Cerca cliente / agenzia</label>
            <Input value={f.search} onChange={(e) => setField("search")(e.target.value)} placeholder="Nome cliente o agenzia…" data-testid="orders-search" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Agenzia</label>
            <Select value={f.agency_id} onValueChange={setField("agency_id")}>
              <SelectTrigger className="w-[190px]" data-testid="orders-filter-agency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le agenzie</SelectItem>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Da mese</label>
            <Input type="month" value={f.from_month} onChange={(e) => setField("from_month")(e.target.value)} className="w-[150px]" data-testid="orders-from-month" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">A mese</label>
            <Input type="month" value={f.to_month} onChange={(e) => setField("to_month")(e.target.value)} className="w-[150px]" data-testid="orders-to-month" />
          </div>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="orders-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Azzera</Button>
        </div>
      </Card>

      {t && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Card className="p-4" data-testid="orders-total-count"><div className="text-xs font-semibold uppercase text-muted-foreground">Ordini</div><div className="mt-1 font-heading text-2xl font-black">{num(t.count)}</div></Card>
          <Card className="p-4" data-testid="orders-total-kwp"><div className="text-xs font-semibold uppercase text-muted-foreground">kWp totali</div><div className="mt-1 font-heading text-2xl font-black">{num(Math.round(t.kwp))}</div></Card>
          <Card className="p-4" data-testid="orders-total-pannelli"><div className="text-xs font-semibold uppercase text-muted-foreground">Pannelli</div><div className="mt-1 font-heading text-2xl font-black">{eurN(t.importo_pannelli_eur)}</div></Card>
          <Card className="p-4" data-testid="orders-total-accessori"><div className="text-xs font-semibold uppercase text-muted-foreground">Accessori/Servizi</div><div className="mt-1 font-heading text-2xl font-black">{eurN(t.importo_accessori_eur)}</div></Card>
          <Card className="p-4" data-testid="orders-total-importo"><div className="text-xs font-semibold uppercase text-muted-foreground">Totale</div><div className="mt-1 font-heading text-2xl font-black">{eur(t.importo_eur)}</div></Card>
        </div>
      )}

      <Card className="p-0" data-testid="orders-table-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>N° Ordine</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Articolo pannelli</TableHead>
                  <TableHead className="text-right">kWp</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  {canMargin && <TableHead className="text-right">Margine %</TableHead>}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.orders || []).map((o) => (
                  <>
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`} className="cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                      <TableCell className="text-muted-foreground">{expanded === o.id ? <ChevronDown className="h-4 w-4" /> : <ChevRight className="h-4 w-4" />}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">
                        {o.crm_numero
                          ? <span data-testid={`order-code-${o.id}`}>{o.crm_numero} <span className="font-normal text-muted-foreground">(Fier: {o.numero_ordine || o.order_ref || "—"})</span></span>
                          : (o.numero_ordine || "—")}
                        {o.crm_scostamento && <Badge variant="destructive" className="ml-1 align-middle text-[9px]" data-testid={`order-scostamento-${o.id}`}>scostamento</Badge>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(o.date)}</TableCell>
                      <TableCell className="font-semibold">
                        {o.partner_id
                          ? <Link to={`/partners/${o.partner_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-primary hover:underline" data-testid={`order-client-link-${o.id}`} title="Apri anagrafica cliente (nuova scheda)">{o.cliente || "—"}</Link>
                          : (o.cliente || "—")}
                        {!o.is_partner && <Badge variant="outline" className="ml-1 text-[10px]">cliente</Badge>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{o.agency_name || "—"}</TableCell>
                      <TableCell className="max-w-[200px] whitespace-normal text-muted-foreground" title={o.articolo || ""}>{o.articolo || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{num(Math.round(o.kwp || 0))}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{eur(o.importo_eur)}</TableCell>
                      {canMargin && <TableCell className="text-right font-mono">{pct(o.margine_commessa_pct)}</TableCell>}
                      <TableCell className="text-right">{o.has_pdf && <FileDown className="ml-auto h-4 w-4 text-primary" />}</TableCell>
                    </TableRow>
                    {expanded === o.id && (
                      <TableRow data-testid={`order-detail-row-${o.id}`}>
                        <TableCell colSpan={COLS} className="bg-muted/30 p-0">
                          <OrderDetail orderId={o.id} canMargin={canMargin} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {(!data?.orders || data.orders.length === 0) && (
                  <TableRow><TableCell colSpan={COLS} className="py-12 text-center text-muted-foreground">Nessun ordine per i filtri selezionati.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {total > PAGE && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm">
            <span className="text-muted-foreground">Pagina {page + 1} di {pages} · {num(total)} ordini</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setExpanded(null); setPage((p) => p - 1); }} data-testid="orders-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => { setExpanded(null); setPage((p) => p + 1); }} data-testid="orders-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
